import { useTranslation } from 'react-i18next';
import { Link, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { ROLE_COLORS, ROLE_ICONS, roleForeground } from './roles';
import type { HoldUse, PendingHoldTap } from './useBoardGrid';
import { useCellName } from './useCellName';

interface RepeatedHoldDialogProps {
  /** The tap waiting to be told what it meant, or null when none is. */
  tap: PendingHoldTap | null;
  /** It meant this use of the hold — the one the setter wants to work on. */
  onPick: (index: number) => void;
  /** It meant the climber comes back to the hold: it joins the run again. */
  onRepeat: () => void;
  /** It meant both hands on it, which is what a second use side by side is. */
  onMatch: () => void;
  /** It meant none of those; leave the circuit as it stands. */
  onCancel: () => void;
  /** Whether the wall is drawn as lines, which is how the hold is named. */
  shaped?: boolean;
}

/**
 * What a tap on a hold the circuit already uses meant.
 *
 * A circuit may come back to a hold — the same one can be its 10th and its 21st,
 * with its own role and its own hand each time — and the board draws one circle
 * per cell, so the picture cannot say which of the two a tap is: the use the
 * setter wants to work on, or another move onto the same hold. Asking is cheaper
 * than a guess they would then have to undo.
 *
 * Coming back to a hold needs **a hold in between**. Two uses side by side are
 * not a lap of the circuit but a match — one hold, two moves, one LED — and that
 * is a role, so where the run cannot come back the dialog offers the match
 * instead (see useBoardGrid#canClimbAgain).
 */
export default function RepeatedHoldDialog({
  tap,
  onPick,
  onRepeat,
  onMatch,
  onCancel,
  shaped = false,
}: RepeatedHoldDialogProps) {
  // Nothing rendered while no tap is waiting: the content names the hold, so
  // there is none to draw — and no closing animation to hold it through.
  if (!tap) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-sm">
        <RepeatedHoldPrompt
          tap={tap}
          onPick={onPick}
          onRepeat={onRepeat}
          onMatch={onMatch}
          shaped={shaped}
        />
      </DialogContent>
    </Dialog>
  );
}

function RepeatedHoldPrompt({
  tap,
  onPick,
  onRepeat,
  onMatch,
  shaped,
}: {
  tap: PendingHoldTap;
  onPick: (index: number) => void;
  onRepeat: () => void;
  onMatch: () => void;
  shaped: boolean;
}) {
  const { t } = useTranslation();
  const cell = useCellName(tap, shaped);
  const matched = tap.uses.at(-1)?.role === 'match';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('climbs.board.circuit.repeated.title')}</DialogTitle>
        <DialogDescription>
          {tap.uses.length > 1
            ? t('climbs.board.circuit.repeated.multiple', { cell, count: tap.uses.length })
            : t('climbs.board.circuit.repeated.single', { cell })}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-1">
        {/* Every place the cell takes in the run, so the setter says which one
            they mean before anything is done to it. */}
        {tap.uses.map((use) => (
          <UseRow key={use.index} use={use} onClick={() => onPick(use.index)} />
        ))}

        {/* And the other thing the tap could have meant. Where the run cannot come
            back to the hold — there is no hold in between — what it would have
            been is a match, so that is what is offered in its place. */}
        {tap.canRepeat && (
          <PromptRow
            icon={<Plus className="size-4" aria-hidden />}
            label={t('climbs.board.circuit.repeated.add')}
            onClick={onRepeat}
          />
        )}

        {!tap.canRepeat && tap.canMatch && (
          <PromptRow
            icon={<Link className="size-4" aria-hidden />}
            label={t(
              matched
                ? 'climbs.board.circuit.repeated.unmatch'
                : 'climbs.board.circuit.repeated.match'
            )}
            onClick={onMatch}
          />
        )}

        {!tap.canRepeat && (
          <p className="px-2 text-xs text-muted-foreground">
            {t('climbs.board.circuit.repeated.needsAHoldBetween')}
          </p>
        )}
      </div>
    </>
  );
}

/** One of the places the cell already takes, named by the number it wears. */
function UseRow({ use, onClick }: { use: HoldUse; onClick: () => void }) {
  const { t } = useTranslation();
  const Icon = ROLE_ICONS[use.role];
  const color = ROLE_COLORS[use.role];

  return (
    <PromptRow
      onClick={onClick}
      icon={
        <span
          className="flex size-6 items-center justify-center rounded-full text-[10px] font-bold tabular-nums"
          style={{ backgroundColor: color, color: roleForeground(color) }}
        >
          {/* A foot is stood on throughout rather than moved to, so it wears no
              number: it is named by what it is instead. */}
          {use.label || <Icon className="size-3" aria-hidden />}
        </span>
      }
      label={
        use.label
          ? t('climbs.board.circuit.repeated.use', { order: use.label })
          : t(`climbs.holdRoles.${use.role}.label`)
      }
    />
  );
}

function PromptRow({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors hover:bg-muted/60"
    >
      <span className="flex size-6 items-center justify-center text-muted-foreground">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
